import { useMemo } from 'react';

import { PermissionRoles } from '@gearbelt/global';

import { useUserStore } from '@/stores/user.store';

// Permission flags derived from the GB user-types matrix. Organised by module
// so that when the matrix diverges (e.g. "Members can now manage Lists") we
// change the rule in one place instead of every call site.
//
// Two shapes:
// - Structural modules (`canManage*`, `canEdit*Settings`, `canExportCsv`) —
//   Admin-only today.
// - Record-owned modules (`canEdit{Record}`) — Admin always, Member only if
//   they authored the record. Pass the record's owner user id.
export interface Permissions {
  roles: PermissionRoles[];
  /** Sitemate staff — cascades down and satisfies every check. */
  isSuperUser: boolean;
  /** GearbeltAdmin, or higher via SitemateSuperUser cascade. */
  isAdmin: boolean;
  /** GearbeltMember without any higher role. */
  isMember: boolean;

  // Structural modules — matrix: Admin only.
  canManageAssets: boolean;
  canManageLocations: boolean;
  canManageLists: boolean;
  canManageUsers: boolean;
  canManageDeploymentRules: boolean;
  canManageInspectionForms: boolean;
  canManageRepairForms: boolean;
  canManageScheduleForms: boolean;
  canManageSchedules: boolean;
  canEditWorkspaceSettings: boolean;
  canEditStartSettings: boolean;
  canExportCsv: boolean;

  // Record modules — matrix: Admin always; Member only if they authored
  // the record. Pass `record.userId` (or equivalent owner id).
  canEditIssue: (ownerUserId: string | null | undefined) => boolean;
  canEditWorkOrder: (ownerUserId: string | null | undefined) => boolean;
  canEditDocument: (ownerUserId: string | null | undefined) => boolean;
  canEditInspectionRecord: (ownerUserId: string | null | undefined) => boolean;
  canEditRepairRecord: (ownerUserId: string | null | undefined) => boolean;
  canEditScheduleRecord: (ownerUserId: string | null | undefined) => boolean;
  canEditCheckRecord: (ownerUserId: string | null | undefined) => boolean;
}

export function usePermissions(): Permissions {
  const { user } = useUserStore();

  return useMemo<Permissions>(() => {
    const roles = user?.roles ?? [];
    const isSuperUser = roles.includes(PermissionRoles.SitemateSuperUser);
    const isAdmin = isSuperUser || roles.includes(PermissionRoles.GearbeltAdmin);
    const isMember = !isAdmin && roles.includes(PermissionRoles.GearbeltMember);

    const canEditOwnedRecord = (ownerUserId: string | null | undefined): boolean => {
      if (isAdmin) return true;
      if (!user || !isMember) return false;
      return Boolean(ownerUserId) && ownerUserId === user.id;
    };

    return {
      roles,
      isSuperUser,
      isAdmin,
      isMember,

      canManageAssets: isAdmin,
      canManageLocations: isAdmin,
      canManageLists: isAdmin,
      canManageUsers: isAdmin,
      canManageDeploymentRules: isAdmin,
      canManageInspectionForms: isAdmin,
      canManageRepairForms: isAdmin,
      canManageScheduleForms: isAdmin,
      canManageSchedules: isAdmin,
      canEditWorkspaceSettings: isAdmin,
      canEditStartSettings: isAdmin,
      canExportCsv: isAdmin,

      canEditIssue: canEditOwnedRecord,
      canEditWorkOrder: canEditOwnedRecord,
      canEditDocument: canEditOwnedRecord,
      canEditInspectionRecord: canEditOwnedRecord,
      canEditRepairRecord: canEditOwnedRecord,
      canEditScheduleRecord: canEditOwnedRecord,
      canEditCheckRecord: canEditOwnedRecord,
    };
  }, [user]);
}
